<template>

  <div>
    <supplier-list-add-new
      :is-add-new-sidebar-active.sync="isAddNewSidebarActive"
      :type-options="typeOptions"
      @refetch-data="refetchData"
    />

    <!-- Filters -->
    <supplier-list-filters
      :type-filter.sync="typeFilter"
      :type-options="typeOptions"
      :status-filter.sync="statusFilter"
      :status-options="statusOptions"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>
          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />
              <b-button
                variant="primary"
                @click="isAddNewSidebarActive = true"
              >
                <span class="text-nowrap">Add Supplier</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refSupplierListTable"
        class="position-relative"
        :items="fetchSuppliers"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >

        <!-- Column: Image -->
        <template #cell(image)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.image_thumbnail"
                square
                rounded="sm"
                text="S"
              />
            </template>
          </b-media>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          <b-link
            :to="{ name: 'apps-suppliers-edit', params: { id: data.item.id }}"
          >
            {{ data.item.name }}
          </b-link>
        </template>

        <!-- Column: Created at -->
        <template #cell(id)="data">
          {{ data.item.created_at }}
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-link
            :to="{ name: 'apps-suppliers-edit', params: { id: data.item.id }}"
          >
            <feather-icon
              :id="`supplier-row-${data.item.id}-edit-icon`"
              icon="EditIcon"
              size="16"
              class="mx-1"
            />
          </b-link>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >

            <b-pagination
              v-model="currentPage"
              :total-rows="totalSuppliers"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>

import {
  BAvatar, BButton, BCard, BCol, BFormInput, BLink, BMedia, BPagination, BRow, BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import {
  computed, onMounted, onUnmounted, ref,
} from '@vue/composition-api'

import SupplierListAddNew from './SupplierListAddNew.vue'
import SupplierListFilters from './SupplierListFilters.vue'
import supplierStoreModule from '../supplierStoreModule'
import useSuppliersList from './useSuppliersList'

export default {
  components: {
    SupplierListAddNew,
    SupplierListFilters,

    BAvatar,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BLink,
    BMedia,
    BRow,
    BPagination,
    BTable,

    vSelect,
  },
  methods: {
  },
  setup(props, { root }) {
    const SUPPLIER_APP_STORE_MODULE = 'app-supplier'

    // Register module
    if (!store.hasModule(SUPPLIER_APP_STORE_MODULE)) store.registerModule(SUPPLIER_APP_STORE_MODULE, supplierStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SUPPLIER_APP_STORE_MODULE)) store.unregisterModule(SUPPLIER_APP_STORE_MODULE)
    })

    const isAddNewSidebarActive = ref(false)
    const userRole = JSON.parse(localStorage.getItem('userData')).role
    const typeOptions = computed(() => {
      const options = root.$store.getters['app-supplier/typeOptions']
      return options
    })

    const statusOptions = computed(() => {
      if (userRole === 'platform-manager') {
        return [
          { label: 'Draft', value: 'Draft' },
          { label: 'Pending', value: 'Pending' },
          { label: 'Approved', value: 'Approved' },
          { label: 'Removed', value: 'Removed' },
        ]
      }
      return [
        { label: 'Draft', value: 'Draft' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Approved', value: 'Approved' },
      ]
    })

    const {
      fetchSuppliers,
      tableColumns,
      perPage,
      currentPage,
      totalSuppliers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSupplierListTable,
      refetchData,

      typeFilter,
      statusFilter,
    } = useSuppliersList()

    const optionsInit = () => {
      root.$store.dispatch('app-supplier/fetchFormOptions')
    }

    onMounted(optionsInit)

    return {
      isAddNewSidebarActive,

      fetchSuppliers,
      tableColumns,
      perPage,
      currentPage,
      totalSuppliers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSupplierListTable,
      refetchData,

      typeOptions,
      statusOptions,

      // Filter
      typeFilter,
      statusFilter,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
@import '@core/scss/vue/libs/vue-select.scss';
</style>
